import {Component,OnInit,ViewChild} from '@angular/core';
import {MessageAdmin,MessageAdminType} from "@domain/admin/bibliotheque/notification/message-admin";
import {FloatingButtonAction,TypeAction} from "@share/component/floating-button/floating-button";
import {BehaviorSubject} from "rxjs";
import {AccueilDetailService} from "@components/admin/bibliotheque/notifications/accueil/accueil-detail/accueil-detail.service";
import {Router} from "@angular/router";
import {PageHeaderItem} from "@share/component/page-header/page-header";
import {TranslateService} from "@ngx-translate/core";
import {NgForm} from "@angular/forms";
import {ListView} from "@domain/common/list-view";
import {MessageAdminRole} from "@domain/admin/bibliotheque/notification/message-admin-role";
import {RoleItemComponent} from "@components/admin/bibliotheque/notifications/accueil/accueil-detail/role-item/role-item.component";

/**
 * Enum des onglets
 */
export enum Onglets {
    ACCUEIL = "Accueil"
}

/**
 * Composant d'affichage de la page détail des accueils
 *
 * @author Lionel Gimenez
 * @date 24/03/2026
 */
@Component({
    host: {'data-test-id': 'accueil-detail'},
    selector: 'accueil-detail',
    templateUrl: './accueil-detail.component.html',
    providers: [AccueilDetailService]
})
export class AccueilDetailComponent implements OnInit {
    /** Message d'accueil */
    messageAdmin: MessageAdmin;

    /** Liste des types de message pour le select */
    listeTypes: Array<{ id: number, libelle: string }>;

    /** Liste des rôles */
    listeRole: ListView<MessageAdminRole, RoleItemComponent>;

    /** Liste statique des onglets */
    public static listeOnglets: Array<PageHeaderItem> = [{
        code: Onglets.ACCUEIL,
        libelle: 'admin.bibliotheque.notifications.accueil.detail.onglets.generalites',
        url: '/Admin/Bibliotheque/Notification/Accueil/'
    }];

    /** Liste des onglets */
    listeTabItems: Array<PageHeaderItem>;

    /** Onglet courant */
    selectedItem: PageHeaderItem = {
        ... AccueilDetailComponent.listeOnglets[0],
        selected: true
    };

    /** Liste des actions possibles pour le floatingButton en bas à droite */
    listeActions: BehaviorSubject<Array<FloatingButtonAction>> = new BehaviorSubject<Array<FloatingButtonAction>>(null);

    /** Formulaire de la page */
    @ViewChild('form') form: NgForm;

    /**
     * Constructeur
     *
     * @param accueilDetailService le service de gestion des accueils
     * @param router le router
     * @param translateService le service de traduction
     */
    constructor(
        public accueilDetailService: AccueilDetailService,
        public router: Router,
        public translateService: TranslateService,
    ) {
    }

    /**
     * Hook initialisation
     */
    ngOnInit(): void {
        //Chargement de la liste des onglets
        this.listeTabItems = [this.selectedItem];

        //Initialisation de la liste des types de message
        this.listeTypes = [
            {id: MessageAdminType.INFORMATION, libelle: this.translateService.instant('admin.bibliotheque.notifications.accueil.detail.typeOption.information')},
            {id: MessageAdminType.CONFIRMATION, libelle: this.translateService.instant('admin.bibliotheque.notifications.accueil.detail.typeOption.confirmation')},
            {id: MessageAdminType.AVERTISSEMENT, libelle: this.translateService.instant('admin.bibliotheque.notifications.accueil.detail.typeOption.avertissement')},
            {id: MessageAdminType.ALERTE, libelle: this.translateService.instant('admin.bibliotheque.notifications.accueil.detail.typeOption.alerte')}
        ];

        //Définition de la liste des actions possibles
        this.listeActions.next([{
            type: TypeAction.PRIMARY,
            icone: 'nio icon-sauvegarde',
            libelle: 'global.actions.enregistrer',
            doAction: () => this.accueilDetailService.save(),
            isDisabled: () => !this.form?.valid
        },{
            type: TypeAction.SECONDARY,
            icone: 'material-icons-outlined',
            iconeName: 'save_as',
            libelle: 'global.actions.dupliquer',
            doAction: () => this.accueilDetailService.duplicate(),
            isVisible: () => !this.accueilDetailService.isCreation
        },{
            type: TypeAction.SECONDARY,
            icone: 'nio icon-suppression',
            libelle: 'global.actions.supprimer',
            doAction: () => this.accueilDetailService.delete(),
            isVisible: () => !this.accueilDetailService.isCreation
        },{
            type: TypeAction.SECONDARY,
            icone: 'material-icons-outlined',
            iconeName: 'email',
            libelle: 'admin.bibliotheque.notifications.accueil.detail.emailBouton',
            doAction: () => this.accueilDetailService.sendEmail(),
            isVisible: () => !this.accueilDetailService.isCreation
        }]);

        //On charge l'accueil
        this.accueilDetailService.loadAccueilByRoute();

        //Initialisation de la liste des rôles
        this.listeRole = this.accueilDetailService.initListeRoles();
    }

    /**
     * Changement d'onglet
     *
     * @param selectedItem Onglet sélectionné
     */
    onSelectedItemChange(selectedItem: PageHeaderItem) {
        //Mise à jour de l'onglet sélectionné
        this.selectedItem = selectedItem;
    }

    /**
     * Handler du retour
     */
    onGoBack() {
        //Retour à la liste des accueils
        this.router.navigate(['Admin/Bibliotheque/Notification/Accueil']);
    }
}
